import {useTranslation} from 'react-i18next';
import {ArrowDown, ArrowDownUpIcon, ArrowUp, Minus} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu';
import {cn} from '../lib/cn';
import type {SortingByVariant} from './types';

export type SortingActionValue = 'asc' | 'desc' | null;

export type SortingActionProps = {
  value?: SortingActionValue;
  onSortChange?: (value: SortingActionValue) => void;
  className?: string;
  open?: boolean;
  variant?: SortingByVariant;
  onOpenChange?: (isOpen: boolean) => void;
};

export function SortingAction({
  value,
  onSortChange,
  className,
  open,
  variant = 'by_other',
  onOpenChange,
}: SortingActionProps) {
  const {t} = useTranslation();

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'group/trigger cursor-pointer rounded-sm p-0.5 hover:bg-[var(--chekin-color-surface-input-empty)]',
            className,
          )}
          aria-label="Open sorting menu"
        >
          <ArrowDownUpIcon className="h-4 w-4 text-[var(--chekin-color-gray-1)] group-hover/trigger:text-[var(--chekin-color-brand-navy)]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-full max-w-[256px]" align="start">
        <DropdownMenuItem
          active={value === 'asc'}
          className={cn(value === 'asc' && 'text-[var(--chekin-color-brand-blue)]')}
          onClick={() => onSortChange?.('asc')}
        >
          <ArrowUp className="h-4 w-4" />
          {variant === 'by_text' ? t('sort_a_z') : t('sort_in_asc')}
        </DropdownMenuItem>
        <DropdownMenuItem
          active={value === 'desc'}
          className={cn(value === 'desc' && 'text-[var(--chekin-color-brand-blue)]')}
          onClick={() => onSortChange?.('desc')}
        >
          <ArrowDown className="h-4 w-4" />
          {variant === 'by_text' ? t('sort_z_a') : t('sort_in_desc')}
        </DropdownMenuItem>
        {value && (
          <DropdownMenuItem onClick={() => onSortChange?.(null)}>
            <Minus className="h-4 w-4" />
            {t('clear_sorting')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
